import React, { useEffect, useMemo, useRef, useState } from "react";
import Swal from "sweetalert2";

import TimeSlot from "./time_slot";
import WorkingHoursCompoundSelect from "./working_hour_compound_select";
import V2TabellaTurnoGiornaliero from "./v2_tabella_visualizza_turno_giornaliero";
import { buildBreaksPreview } from "./build_breaks_preview";
import { getUiErrorMessage } from "../services/utils";

import {
  getWorkingHoursCompound,
  addWeeklyWorkingHourSlot,
  patchUpdateWeeklyWorkingHourSlot,
  deleteWeeklyWorkingHoursSlot,
  deleteTimeSlotCompany,
  updateTimeSlot,
} from "../services/api_working_hours";

import {
  isCrossMidnight,
  findOverlapsInDay,
  confirmOverwriteIfNeeded,
  deleteOverlappingSlots,
  splitBreaksAcrossMidnightStrings,
  uiTime,
  apiTime,
  mapApiBreaksToUi,
  normalizeBreaksForApi,
  syncBreaks,
  dayLabelFromWeekDay,
} from "../utils/useWeeklyShiftEditor";




function nextWeekDay(weekDay) {
  if (!weekDay) return 1;
  return weekDay === 7 ? 1 : weekDay + 1;
}

export default function WeekDayEditModal({
  token,
  open,
  onClose,
  weeklyWorkingHour,
  weekDay, // 1..7
  selectedOuId = null,
  allowHiddenBreaks = false,
  onSaved,
  initialTimeSlotId = null,
  defaultMode = null,
}) {
  const dayLabel = dayLabelFromWeekDay(weekDay);

  const [mode, setMode] = useState("working_hour");
  const [workingHoursList, setWorkingHoursList] = useState([]);
  const [selectedWorkingHour, setSelectedWorkingHour] = useState(null);

  const [slotByWeekday, setSlotByWeekday] = useState(null); // sbw day
  const [timeSlots, setTimeSlots] = useState([]); // time_slots day
  const [editingTimeSlotId, setEditingTimeSlotId] = useState(null);

  const [editorKey, setEditorKey] = useState(0);
  const [showEditor, setShowEditor] = useState(true);
  const [loading, setLoading] = useState(false);

  const didInitRef = useRef(false);

  // init
  useEffect(() => {
    if (!open) return;
    didInitRef.current = false;
  }, [open, weekDay, weeklyWorkingHour?.id]);

  useEffect(() => {
    if (!open) return;
    if (!weeklyWorkingHour?.id) return;
    if (!weekDay) return;
    if (didInitRef.current) return;

    const sbw =
      (weeklyWorkingHour?.slots_by_weekday || []).find((x) => x.week_day === weekDay) || null;

    setSlotByWeekday(sbw);

    const ts = Array.isArray(sbw?.time_slots) ? sbw.time_slots : [];
    const hasWorkingHour = !!sbw?.working_hour;

    // apertura da "+"
    if (defaultMode === "working_hour") {
      setMode("working_hour");
      setSelectedWorkingHour(null);
      setTimeSlots([]);
      setEditingTimeSlotId(null);
      setShowEditor(true);
    }

    // apertura da matita
    else if (initialTimeSlotId) {
      setMode("custom");
      setSelectedWorkingHour(null);
      setTimeSlots(ts);
      setEditingTimeSlotId(initialTimeSlotId);
      setShowEditor(true);
    }

    // comportamento normale
    else {
      if (hasWorkingHour) {
        setMode("working_hour");
        setSelectedWorkingHour(typeof sbw.working_hour === "object" ? sbw.working_hour : null);
        setTimeSlots([]);
        setShowEditor(true);
      } else {
        setMode("custom");
        setSelectedWorkingHour(null);
        setTimeSlots(ts);
        setShowEditor(ts.length === 0);
      }
      setEditingTimeSlotId(null);
    }

    setEditorKey((k) => k + 1);
    didInitRef.current = true;
  }, [open, weeklyWorkingHour, weekDay, initialTimeSlotId, defaultMode]);

  useEffect(() => {
    if (!open) return;
    if (!weeklyWorkingHour?.id) return;
    if (!weekDay) return;

    const sbw =
      (weeklyWorkingHour?.slots_by_weekday || []).find(
        (x) => x.week_day === weekDay
      ) || null;

    setSlotByWeekday(sbw);

    if (mode === "custom") {
      const ts = Array.isArray(sbw?.time_slots) ? sbw.time_slots : [];
      setTimeSlots(ts);
    }

  }, [weeklyWorkingHour]);

  // load WH
  useEffect(() => {
    if (!open || !token) return;
    let alive = true;

    (async () => {
      try {
        const list = await getWorkingHoursCompound(token, selectedOuId);
        if (!alive) return;
        setWorkingHoursList(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error(e);
        if (!alive) return;
        setWorkingHoursList([]);
      }
    })();

    return () => {
      alive = false;
    };
  }, [open, token, selectedOuId]);

  // block scroll
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => (document.body.style.overflow = prev);
  }, [open]);

  const editingTimeSlot = useMemo(() => {
    if (!editingTimeSlotId) return null;
    return timeSlots.find((x) => x.id === editingTimeSlotId) || null;
  }, [editingTimeSlotId, timeSlots]);

  const shiftPreviewForEditor = useMemo(() => {
    if (!editingTimeSlot) return { workStartTime: "", workEndTime: "", breaks: [] };
    return {
      workStartTime: uiTime(editingTimeSlot.start_at),
      workEndTime: uiTime(editingTimeSlot.end_at),
      breaks: mapApiBreaksToUi(editingTimeSlot.breaks || []),
    };
  }, [editingTimeSlot]);

  const ensureDaySlot = async (weekDayToEnsure) => {
    const existing =
      (weeklyWorkingHour?.slots_by_weekday || []).find((x) => x.week_day === weekDayToEnsure) ||
      (weekDayToEnsure === weekDay ? slotByWeekday : null);

    if (existing?.id) return existing;

    const created = await addWeeklyWorkingHourSlot(token, {
      week_day: weekDayToEnsure,
      weekly_working_hour: weeklyWorkingHour.id,
      working_hour: null,
      time_slots: [],
    });

    // se è il giorno corrente aggiorno lo state
    if (weekDayToEnsure === weekDay) setSlotByWeekday(created);

    return created;
  };

  const handleClose = async () => {

    setMode("working_hour");
    setSelectedWorkingHour(null);
    setEditingTimeSlotId(null);
    setShowEditor(true);
    setEditorKey((k) => k + 1);
    onClose?.();
  };

  // ---- working hour actions (come le avevi)
  const handleApplyWorkingHour = async () => {
    if (!selectedWorkingHour?.id) return;

    try {
      setLoading(true);
      const sbw = await ensureDaySlot(weekDay);

      const existingTimeSlots = Array.isArray(sbw?.time_slots) ? sbw.time_slots : [];

      if (existingTimeSlots.length > 0) {
      const res = await Swal.fire({
        icon: "warning",
        title: "Sovrascrivere le fasce orarie?",
        text: "È presente una o più fasce orarie manuali. Vuoi sovrascriverle con il turno giornaliero?",
        showCancelButton: true,
        confirmButtonText: "Sovrascrivi",
        cancelButtonText: "Annulla",
        cancelButtonColor: "#d1d5db",
      });

      if (!res.isConfirmed) return;
    }

    const resp = await patchUpdateWeeklyWorkingHourSlot(token, sbw.id, {
      working_hour: selectedWorkingHour.id,
      time_slots: [],
    });

      setSlotByWeekday(resp);
      setMode("working_hour");
      setTimeSlots([]);
      setShowEditor(true);
      setEditingTimeSlotId(null);
      setEditorKey((k) => k + 1);

      await onSaved?.();
      Swal.fire({ position: "center", icon: "success", title: "Salvato!", showConfirmButton: false, timer: 900 });
      onClose();
    } catch (e) {
      console.error(e);
      Swal.fire({ icon: "error", title: "Errore", text: getUiErrorMessage(e) });
    } finally {
      setLoading(false);
    }
  };

  const handleSwitchToCustom = async () => {
    if (!slotByWeekday?.id || !slotByWeekday?.working_hour) {
      setMode("custom");
      return;
    }
    try {
      setLoading(true);
      const resp = await patchUpdateWeeklyWorkingHourSlot(token, slotByWeekday.id, {
        working_hour: null,
        time_slots: [],
      });
      setSlotByWeekday(resp);
      setMode("custom");
      setSelectedWorkingHour(null);
      const ts = Array.isArray(resp?.time_slots) ? resp.time_slots : [];
      setTimeSlots(ts);
      setShowEditor(ts.length === 0);
      setEditingTimeSlotId(null);
      setEditorKey((k) => k + 1);
      await onSaved?.();
    } catch (e) {
      console.error(e);
      Swal.fire({ icon: "error", title: "Errore", text: getUiErrorMessage(e) });
    } finally {
      setLoading(false);
    }
  };

  // ---- delete one slot
  const handleDeleteTimeSlot = async (timeSlotId) => {
    const res = await Swal.fire({
      icon: "warning",
      title: "Eliminare l'orario?",
      showCancelButton: true,
      confirmButtonText: "Sì",
      cancelButtonText: "Annulla",
      cancelButtonColor: "#d1d5db",
    });
    if (!res.isConfirmed) return;

    try {
      setLoading(true);
      await deleteTimeSlotCompany(token, timeSlotId);

      const next = timeSlots.filter((x) => x.id !== timeSlotId);
      setTimeSlots(next);

      if (next.length === 0 && slotByWeekday?.id) {
        await deleteWeeklyWorkingHoursSlot(token, slotByWeekday.id);
        setSlotByWeekday(null);
      }

      setEditingTimeSlotId(null);
      setShowEditor(next.length === 0);
      setEditorKey((k) => k + 1);

      await onSaved?.();
      Swal.fire({ position: "center", icon: "success", title: "Eliminato!", showConfirmButton: false, timer: 900 });
    } catch (e) {
      console.error(e);
      Swal.fire({ icon: "error", title: "Errore", text: getUiErrorMessage(e) });
    } finally {
      setLoading(false);
    }
  };

  /**
   * SALVA su "Conferma fascia oraria"
   * - se end < start => cross-midnight:
   *    - patch/create anchor (start->24:00)
   *    - patch/create next (00:00->end)
   *    - prima controlla overlap su next day e chiede conferma sovrascrittura
   * - se non cross:
   *    - patch/create sul giorno corrente
   *    - (opzionale) se c’era una parte notturna precedente, NON la gestiamo qui: resta a tua logica attuale.
   */
  const handleSaveSlot = async (rawSlot) => {
    try {
      setLoading(true);

      const start = apiTime(rawSlot?.workStartTime);
      const end = apiTime(rawSlot?.workEndTime);
      if (!start || !end) {
          Swal.fire({ icon: "error", title: "Orari non validi" });
          return;
      }

      // ci serve sbw giorno corrente
      const sbwToday = await ensureDaySlot(weekDay);
      const breaksUi = rawSlot?.breaks || [];
      const cross = isCrossMidnight(start, end);

      const overlapsToday = findOverlapsInDay(
        sbwToday?.time_slots || [],
        start,
        cross ? "24:00" : end,
        editingTimeSlotId // escludi se sto modificando
      );

      if (overlapsToday.length > 0) {
        Swal.fire({
          icon: "error",
          title: "Sovrapposizione",
          text: "La fascia oraria si sovrappone a un'altra già esistente nello stesso giorno.",
        });
        setLoading(false);
        return;
      }

      // se era working_hour, sganciamo
      if (sbwToday?.working_hour) {
        await patchUpdateWeeklyWorkingHourSlot(token, sbwToday.id, { working_hour: null, time_slots: [] });
      }

      // --------- CROSS MIDNIGHT
      if (cross) {
        const nextWd = nextWeekDay(weekDay);
        const nextLabel = dayLabelFromWeekDay(nextWd);

        // split pause
        const { b1, b2 } = splitBreaksAcrossMidnightStrings(start, end, breaksUi);

        // overlap check sul GIORNO SUCCESSIVO: [00:00, end)
        const sbwNext = await ensureDaySlot(nextWd);
        if (sbwNext?.working_hour) {
          const res = await Swal.fire({
            icon: "warning",
            title: "Turno giornaliero presente",
            text: `Il giorno ${nextLabel} ha un turno giornaliero associato. Vuoi sostituirlo con una fascia manuale?`,
            showCancelButton: true,
            confirmButtonText: "Sostituisci",
            cancelButtonText: "Annulla",
            cancelButtonColor: "#d1d5db",
          });
          if (!res.isConfirmed) return;

          await patchUpdateWeeklyWorkingHourSlot(token, sbwNext.id, { working_hour: null, time_slots: [] });
        }

        const overlapsNext = findOverlapsInDay(sbwNext?.time_slots || [], "00:00", end, null);
        const okOverwrite = await confirmOverwriteIfNeeded(overlapsNext, nextLabel);
        if (!okOverwrite) return;

        await deleteOverlappingSlots({ token, overlapsList: overlapsNext, deleteTimeSlotCompany });

        // 1) salva/patch ANCHOR sul giorno corrente: start->24:00
        if (editingTimeSlotId) {
          await updateTimeSlot(token, editingTimeSlotId, { start_at: start, end_at: "24:00" });

          // sync pause SOLO del pezzo giorno corrente
          await syncBreaks(
            token,
            editingTimeSlotId,
            mapApiBreaksToUi(editingTimeSlot?.breaks || []),
            b1
          );

          setTimeSlots((prev) =>
            (prev || []).map((ts) =>
              ts.id === editingTimeSlotId ? { ...ts, start_at: start, end_at: "24:00" } : ts
            )
          );
        } else {
          await patchUpdateWeeklyWorkingHourSlot(token, sbwToday.id, {
            working_hour: null,
            time_slots: [
              {
                start_at: start,
                end_at: "24:00",
                // breaks b1
                breaks: normalizeBreaksForApi(b1).map((b) => ({
                  start_at: apiTime(b.start_at),
                  end_at: apiTime(b.end_at),
                  is_visible: b.is_visible ?? true,
                })),
              },
            ],
          });
        }

        // 2) crea SEMPRE sul giorno successivo un time_slot 00:00->end
        await patchUpdateWeeklyWorkingHourSlot(token, sbwNext.id, {
          working_hour: null,
          time_slots: [
            {
              start_at: "00:00",
              end_at: end,
              // breaks b2
              breaks: normalizeBreaksForApi(b2).map((b) => ({
                start_at: apiTime(b.start_at),
                end_at: apiTime(b.end_at),
                is_visible: b.is_visible ?? true,
              })),
            },
          ],
        });

        await onSaved?.();
        Swal.fire({ position: "center", icon: "success", title: "Salvato!", showConfirmButton: false, timer: 800 });

        setEditingTimeSlotId(null);
        setShowEditor(false);
        setEditorKey((k) => k + 1);
        return;
      }

      // --------- NOT CROSS MIDNIGHT
      if (editingTimeSlotId) {
        await updateTimeSlot(token, editingTimeSlotId, { start_at: start, end_at: end });
        await syncBreaks(token, editingTimeSlotId, mapApiBreaksToUi(editingTimeSlot?.breaks || []), breaksUi);

        // aggiorna UI locale subito (così la lista si aggiorna senza refetch)
        setTimeSlots((prev) =>
          (prev || []).map((ts) =>
            ts.id === editingTimeSlotId
            ? {
              ...ts,
              start_at: start,
              end_at: end,
              breaks: (breaksUi || []).map((b) => ({
              ...b,
              start_at: apiTime(b.start_at),
              end_at: apiTime(b.end_at),
              is_visible: b.is_visible ?? true,
              })),
              }
            : ts
          )
        );
      } else {
        const resp = await patchUpdateWeeklyWorkingHourSlot(token, sbwToday.id, {
          working_hour: null,
          time_slots: [
            {
              start_at: start,
              end_at: end,
              breaks: normalizeBreaksForApi(breaksUi).map((b) => ({
                start_at: apiTime(b.start_at),
                end_at: apiTime(b.end_at),
                is_visible: b.is_visible ?? true,
              })),
            },
          ],
        });

        // aggiorna local state (solo giorno corrente)
        setSlotByWeekday(resp);
        setTimeSlots(Array.isArray(resp?.time_slots) ? resp.time_slots : []);
      }

      await onSaved?.();
      Swal.fire({ position: "center", icon: "success", title: "Salvato!", showConfirmButton: false, timer: 800 });

      setEditingTimeSlotId(null);
      setShowEditor(false);
      setEditorKey((k) => k + 1);
    } catch (e) {
      console.error(e);
      Swal.fire({ icon: "error", title: "Errore", text: getUiErrorMessage(e) });
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  const timeSlotsForList = (timeSlots || []).slice().sort((a, b) => String(a.start_at || "").localeCompare(String(b.start_at || "")));

  return (
    <div
      className="fixed inset-0 z-[999] bg-black/40 px-4 py-6 overflow-y-auto"
      role="dialog"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
      style={{ marginTop: 0 }}
    >
      <div className="mx-auto w-full max-w-4xl">
        <div className="rounded-xl bg-white shadow-xl border border-gray-200 overflow-visible">
          {/* HEADER */}
          <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 rounded-t-xl bg-indigo-100">
            <div className="text-md font-semibold uppercase text-indigo-800">
              {dayLabel} — {weeklyWorkingHour?.name || ""}
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleClose}
                className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 uppercase"
              >
                Chiudi
              </button>
            </div>
          </div>

          {/* BODY */}
          <div className="px-5 py-4 space-y-4">
            {/* Tabs */}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setMode("working_hour")}
                className={`px-3 py-2 rounded-lg text-sm font-semibold border ${
                  mode === "working_hour"
                    ? "bg-indigo-600 text-white border-indigo-600"
                    : "bg-white text-indigo-700 border-indigo-200 hover:bg-indigo-50"
                }`}
              >
                Associa Turno Giornaliero
              </button>

              <button
                type="button"
                onClick={handleSwitchToCustom}
                className={`px-3 py-2 rounded-lg text-sm font-semibold border ${
                  mode === "custom"
                    ? "bg-indigo-600 text-white border-indigo-600"
                    : "bg-white text-indigo-700 border-indigo-200 hover:bg-indigo-50"
                }`}
              >
                Fasce Manuali
              </button>
            </div>

            {mode === "working_hour" ? (
              <div className="rounded-lg border border-gray-200 bg-white p-4 overflow-visible">
                <div className="text-sm font-semibold uppercase text-indigo-800">
                  Turno giornaliero da associare
                </div>

                <div className="mt-3 relative z-[1000] overflow-visible">
                  <WorkingHoursCompoundSelect
                    key={`wh-select-${selectedOuId || "generic"}`}
                    token={token}
                    permission="payrolls"
                    sourceOu={selectedOuId ?? null}
                    value={selectedWorkingHour}
                    onChange={setSelectedWorkingHour}
                  />
                </div>

                {selectedWorkingHour && (
                  <div className="mt-3">
                    <V2TabellaTurnoGiornaliero selectedWorkingHour={selectedWorkingHour} />
                  </div>
                )}

                <div className="mt-4 flex justify-end">
                  <button
                    type="button"
                    disabled={!selectedWorkingHour?.id || loading}
                    onClick={handleApplyWorkingHour}
                    className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500 disabled:opacity-60"
                  >
                    {loading ? "Salvo..." : "Applica"}
                  </button>
                </div>
              </div>
            ) : (
              <div className="rounded-lg border border-gray-200 bg-white p-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold uppercase text-indigo-800">
                    {editingTimeSlotId ? "Modifica fascia" : "Aggiungi fascia"}
                  </div>

                  {!editingTimeSlotId && timeSlotsForList.length > 0 && !showEditor && (
                    <button
                      type="button"
                      onClick={() => {
                        setEditingTimeSlotId(null);
                        setShowEditor(true);
                        setEditorKey((k) => k + 1);
                      }}
                      className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-500"
                    >
                      + Aggiungi Fascia
                    </button>
                  )}
                </div>

                <div className="text-xs text-gray-500 mt-1">
                  Clicca <b>Conferma fascia oraria</b> per salvare
                </div>

                {(showEditor || editingTimeSlotId) && (
                  <div className="mt-3">
                    <TimeSlot
                      key={`timeslot-${editorKey}-${editingTimeSlotId || "new"}`}
                      shiftPreview={shiftPreviewForEditor}
                      allowHiddenBreaks={allowHiddenBreaks}
                      onCancelEdit={() => {
                        setEditingTimeSlotId(null);
                        setShowEditor(timeSlotsForList.length === 0);
                        setEditorKey((k) => k + 1);
                      }}
                      onShiftPreview={handleSaveSlot}
                      onSaveEdit={() => {}}
                      onAssignWh={"assignWhComponent"} // consenti cross-midnight nel TimeSlot
                      onWeekShiftComponent={"weekShiftComponent"}
                    />
                  </div>
                )}

                {timeSlotsForList.length > 0 && (
                  <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-3">
                    <div className="text-xs font-semibold uppercase text-gray-700 mb-2">Fasce inserite</div>

                    <div className="space-y-2 max-h-[45vh] overflow-y-auto pr-1">
                      {timeSlotsForList.map((ts) => (
                        <div key={ts.id} className="rounded-lg border border-gray-200 bg-white px-3 py-2">
                          <div className="grid grid-cols-2 sm:grid-cols-3 items-center gap-2">
                            <div className="text-sm font-semibold text-gray-800 min-w-0">
                              {uiTime(ts.start_at)} - {uiTime(ts.end_at)}
                              <span className="ml-2 text-[11px] font-semibold text-indigo-700">{dayLabel}</span>
                            </div>

                            <div className="hidden sm:flex justify-self-center" />

                            <div className="flex w-full items-center justify-end gap-2 w-28">
                              <button
                                type="button"
                                disabled={loading}
                                onClick={() => {
                                  setEditingTimeSlotId(ts.id);
                                  setShowEditor(true);
                                  setEditorKey((k) => k + 1);
                                }}
                                className="text-xs font-semibold text-indigo-700 hover:text-indigo-600 disabled:opacity-60"
                              >
                                Modifica
                              </button>
                              <button
                                type="button"
                                disabled={loading}
                                onClick={() => handleDeleteTimeSlot(ts.id)}
                                className="text-xs font-semibold text-red-600 hover:text-red-500 disabled:opacity-60"
                              >
                                Elimina
                              </button>
                            </div>
                          </div>

                          <div className="mt-1">{buildBreaksPreview(mapApiBreaksToUi(ts.breaks || []))}</div>
                        </div>
                      ))}
                    </div>

                    <div className="mt-3 flex justify-end">
                      <button
                        type="button"
                        onClick={handleClose}
                        className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500"
                      >
                        Fatto
                      </button>
                    </div>
                  </div>
                )}

                {timeSlotsForList.length === 0 && (
                  <div className="mt-3 flex justify-end">
                    <button
                      type="button"
                      onClick={handleClose}
                      className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500"
                    >
                      Fatto
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}